import React from 'react';

// Shared skeleton primitives. Replace the generic 3-ring LoadingScreen
// on screens whose layout is known ahead of time so the user sees the
// shape they're about to read instead of a spinner. Per Apple HIG /
// Material guidance, this reduces perceived load time by ~25%.
//
// All skeletons inherit colour from CSS variables so they retint with
// the active theme. The shimmer keyframe is injected once on first
// import (same pattern as ToastContext); browsers honoring
// prefers-reduced-motion get a static block.

let injected = false;
function injectStyles() {
  if (injected) return;
  injected = true;
  if (typeof document === 'undefined') return;
  if (document.getElementById('bb-skeleton-anim')) return;
  const sheet = document.createElement('style');
  sheet.id = 'bb-skeleton-anim';
  sheet.textContent = `
@keyframes bbShimmer {
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
}
[data-bb-skeleton] {
  background: linear-gradient(100deg,
    var(--surface-high, #E8DDD0) 0%,
    var(--surface-container, #FAF6F1) 42%,
    var(--primary-pale, #F5D6E0) 50%,
    var(--surface-container, #FAF6F1) 58%,
    var(--surface-high, #E8DDD0) 100%);
  background-size: 200% 100%;
  animation: bbShimmer 1.8s cubic-bezier(0.45,0,0.55,1) infinite;
  border-radius: 8px;
}
@media (prefers-reduced-motion: reduce) {
  [data-bb-skeleton] { animation: none !important; }
}
`;
  document.head.appendChild(sheet);
}
// Run at module-import time so even server-side imports don't push it
// to mount-time (idempotent guard above keeps it safe).
injectStyles();

/**
 * Base shimmer block. Pass width/height as numbers (px) or strings
 * (any CSS length). `radius` overrides the default 6px.
 */
export function Skeleton({ width = '100%', height = 14, radius = 8, style, ...rest }) {
  return (
    <div
      data-bb-skeleton
      aria-hidden="true"
      style={{
        width,
        height,
        borderRadius: radius,
        ...style,
      }}
      {...rest}
    />
  );
}

// ── Composed skeletons ──────────────────────────────────────────────

/** Greeting + date eyebrow that sits at the top of WeekHome / Calendar / Profile / Progress. */
export function HeroSkeleton({ style } = {}) {
  return (
    <div style={{ padding: 'calc(116px + var(--safe-top, 0px)) 24px 16px', ...style }} aria-hidden="true">
      {/* Chips row: streak / cycle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <Skeleton width={92} height={26} radius={999} />
        <Skeleton width={108} height={22} radius={999} />
      </div>
      <div style={{ height: 14 }} />
      {/* Week dots row */}
      <Skeleton width="100%" height={20} radius={4} />
      <div style={{ height: 16 }} />
      {/* Greeting (matches new 26px h1) */}
      <Skeleton width="78%" height={28} radius={6} />
      <div style={{ height: 10 }} />
      {/* Dateline */}
      <Skeleton width={150} height={11} radius={4} />
    </div>
  );
}

/** Today's Practice card (mauve hero band over a white footer). */
export function SessionCardSkeleton() {
  return (
    <section style={{ padding: '0 24px 24px' }} aria-hidden="true">
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 22,
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(45,26,31,0.04), 0 18px 36px -20px rgba(45,26,31,0.16)',
      }}>
        {/* Mauve hero — match the live card's 18/22 padding and 26px title */}
        <div style={{ background: 'var(--card-mauve, #8B4A5A)', padding: '20px 22px 26px' }}>
          <Skeleton width={120} height={10} radius={4} style={{ background: 'rgba(255,255,255,0.28)' }} />
          <div style={{ height: 10 }} />
          <Skeleton width="72%" height={26} radius={6} style={{ background: 'rgba(255,255,255,0.32)' }} />
        </div>
        <div style={{ padding: '16px 20px 18px' }}>
          {/* Meta row chips */}
          <div style={{ display: 'flex', gap: 8 }}>
            <Skeleton width={84} height={22} radius={999} />
            <Skeleton width={70} height={22} radius={999} />
          </div>
          <div style={{ height: 12 }} />
          {/* Movement pills */}
          <div style={{ display: 'flex', gap: 7 }}>
            <Skeleton width={72} height={20} radius={999} />
            <Skeleton width={64} height={20} radius={999} />
            <Skeleton width={68} height={20} radius={999} />
          </div>
          <div style={{ height: 14 }} />
          {/* CTA — matches the new 50-min-height filled pill */}
          <Skeleton height={50} radius={999} />
        </div>
      </div>
    </section>
  );
}

/** Three stat tiles in a row, used by Progress + CalendarScreen. */
export function StatsRowSkeleton() {
  return (
    <div style={{ display: 'flex', gap: 12 }} aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            flex: 1,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 16,
            padding: '14px 12px',
          }}
        >
          <Skeleton width={32} height={26} />
          <div style={{ height: 8 }} />
          <Skeleton width="80%" height={10} radius={4} />
        </div>
      ))}
    </div>
  );
}

/** A simple stack of N text-block placeholders for content lists. */
export function ListSkeleton({ rows = 3 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} height={56} radius={12} />
      ))}
    </div>
  );
}
